import React, {useEffect} from "react";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {
    addBotMessageManually,
    clearChatHistory,
    focusVehicle,
    getWidgetSettings,
    initializeState,
    initMessage,
    isBotInitializedSelector,
    isBotOpenSelector,
    isMobileSelector,
    markWelcomeBubbleTimeoutAsElapsed,
    openChatbot,
    sendMessageToBot,
    showWidgetSelector,
    updateFirstBotMessage,
    updateFullUrl,
    websiteLocationSelector,
    initialMessageSelector,
    verticalOffsetSelector,
    widgetPositionSelector,
    horizontalOffsetSelector
} from "../../chatbotSlice";
import {LiveChat} from "../LiveChat";
import {sendChangeWidgetPositionEvent} from "../../chatbotUtil";

export const MOBILE_WIDGET_HEIGHT = '100%'
export const MOBILE_WIDGET_WIDTH = '100%'

export const WIDGET_HEIGHT = '684px'
export const WIDGET_WIDTH = '384px'

export const BUBBLE_WITH_STARTERS_WIDTH = '370px'
export const BUBBLE_WITH_STARTERS_HEIGHT = '450px'

export const BUBBLE_WIDTH = '120px'
export const BUBBLE_HEIGHT = '120px'

export const App: React.FC = () => {

    const dispatch = useAppDispatch();

    const isMobile = useAppSelector(isMobileSelector);
    const isBotOpen = useAppSelector(isBotOpenSelector);
    const showWidget = useAppSelector(showWidgetSelector);
    const isBotInitialized = useAppSelector(isBotInitializedSelector);
    const websiteLocation = useAppSelector(websiteLocationSelector);
    const initialBotMessage = useAppSelector(initialMessageSelector);
    const widgetPosition = useAppSelector(widgetPositionSelector)
    const verticalOffsetPx = useAppSelector(verticalOffsetSelector);
    const horizontalOffsetPx = useAppSelector(horizontalOffsetSelector);

    useEffect(() => {
        window.addEventListener('message', handleMessage)
        initializeWidget()

        return () => {
            window.removeEventListener('message', handleMessage)
        }

    }, [])

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            dispatch(markWelcomeBubbleTimeoutAsElapsed());
        }, 5000);

        return () => clearTimeout(timeoutId);
    }, [dispatch])

    const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'initialization') {
            dispatch(initializeState({
                ...event.data.storage,
                botId: event.data.botId,
                profile: event.data.profile,
                platformBaseURL: event.data.platformBaseURL,
                llmEngineBaseURL: event.data.llmEngineBaseURL,
                fullUrl: event.data.fullUrl,
                convoStartersTimeout: event.data.convoStartersTimeout,
                disableConvoStartersButtonsOnChatOnMobile: event.data.disableConvoStartersButtonsOnChatOnMobile,
                disableConvoStartersButtonsOnChatOnDesktop: event.data.disableConvoStartersButtonsOnChatOnDesktop,
                disableConvoStartersButtonsOnBubbleOnMobile: event.data.disableConvoStartersButtonsOnBubbleOnMobile,
                disableConvoStartersButtonsOnBubbleOnDesktop: event.data.disableConvoStartersButtonsOnBubbleOnDesktop,
                disableFirstMessageOnBubbleOnMobile: event.data.disableFirstMessageOnBubbleOnMobile,
                disableFirstMessageOnBubbleOnDesktop: event.data.disableFirstMessageOnBubbleOnDesktop,
                isMobile: event.data.isMobile,
                websiteLocation: event.data.url
            }));
            dispatch(getWidgetSettings(event.data.botId))
            window.parent.postMessage(
                {
                    type: 'widget-init-complete',
                },
                '*',
            )
        } else if (event.data.type === 'update-bot-first-message') {
            dispatch(updateFirstBotMessage(event.data.message));
        } else if (event.data.type === 'update-bot-focused-vehicle') {
            dispatch(focusVehicle({VIN: event.data.VIN ?? event.data.vinNumber, VRM: event.data.VRM}));
        } else if (event.data.type === 'open-widget') {
            dispatch(openChatbot());
        } else if (event.data.type === 'send-message-to-widget') {
            dispatch(sendMessageToBot(event.data.userMessage));
        } else if (event.data.type === 'add-bot-message') {
            dispatch(addBotMessageManually(event.data.botMessage));
        } else if (event.data.type === 'clear-chat-history') {
            dispatch(clearChatHistory());
        } else if (event.data.type === 'update-location') {
            dispatch(updateFullUrl(event.data.url));
        }
    }

    const initializeWidget = () => {
        window.parent.postMessage(
            {
                type: 'widget-init-request',
            },
            '*',
        )
    }

    useEffect(() => {
        const verticalOffset = isMobile ? (isBotOpen ? 0 : verticalOffsetPx) : verticalOffsetPx
        const horizontalOffset = isMobile ? (isBotOpen ? 0 : horizontalOffsetPx) : horizontalOffsetPx
        sendChangeWidgetPositionEvent(widgetPosition, horizontalOffset, verticalOffset, websiteLocation)
        if (isBotOpen) {
            dispatch(initMessage([]))
        }

        if (websiteLocation) {
            window.parent.postMessage(
                {
                    type: 'toggle-widget',
                    isWidgetOpen: isBotOpen
                },
                websiteLocation
            )
            resizeWidget()
        }
    }, [isBotOpen, websiteLocation, initialBotMessage])

    useEffect(() => {
        resizeWidget()
    }, [])

    const resizeWidget = () => {
        const widgetOffsetWidth = isBotOpen ? WIDGET_WIDTH : BUBBLE_WIDTH
        const widgetOffsetHeight = isBotOpen ? WIDGET_HEIGHT : BUBBLE_HEIGHT
        const mobileWidgetOffsetWidth = isBotOpen ? MOBILE_WIDGET_WIDTH : BUBBLE_WIDTH
        const mobileWidgetOffsetHeight = isBotOpen ? MOBILE_WIDGET_HEIGHT : BUBBLE_HEIGHT

        if (isMobile) {
            resizeWidgetEvent(mobileWidgetOffsetWidth, mobileWidgetOffsetHeight)
        } else {
            resizeWidgetEvent(widgetOffsetWidth, widgetOffsetHeight)
        }
    }

    const resizeWidgetEvent = (width: string, height: string) => {
        if (websiteLocation) {
            window.parent.postMessage(
                {
                    type: 'resize-widget',
                    width: width,
                    height: height,
                    isFullScreenWidget: false
                },
                websiteLocation
            )
        }
    }

    if (isBotInitialized && showWidget) {
        return <LiveChat resizeWidget={resizeWidgetEvent}/>
    } else {
        return <></>
    }
}
